import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, LockIcon, PencilIcon, PlusIcon, PowerIcon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { EncabezadoPanel, Panel } from '../components/ui/Basicos';
import { EditorSucursal } from '../components/almacenes/EditorSucursal';
import { useDatos } from '../contexts/DatosContext';
import { abogadoPorId } from '../data/abogados';
import { contarCajas } from '../utils/almacen';
import type { Sucursal } from '../types';
function sucursalVacia(): Sucursal {
  return {
    id: `suc-${Date.now()}`,
    clave: '',
    nombre: '',
    direccion: '',
    ciudad: '',
    estado: '',
    telefono: '',
    responsableId: '',
    activa: true
  };
}
export function ConfigSucursales() {
  const {
    rol,
    sucursales,
    almacenes,
    expedientes,
    agregarSucursal,
    actualizarSucursal,
    alternarSucursal
  } = useDatos();
  const [editando, setEditando] = useState<{
    sucursal: Sucursal;
    esNueva: boolean;
  } | null>(null);
  const [aviso, setAviso] = useState('');
  if (rol !== 'Administrador') {
    return <>
        <EncabezadoPagina titulo="Sucursales y almacenes" descripcion="Configuración del despacho" data-id="element-1293" data-mp="37qq9uhhs2dwr" />
        <Panel data-id="element-1294" data-mp="3gt9acuy3cefu">
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center" data-id="element-1295" data-mp="3gtrbo5zm1ium">
            <LockIcon className="h-6 w-6 text-gris-300" aria-hidden="true" data-id="element-1296" data-mp="2k38l6utt7vy0" />
            <p className="text-sm font-semibold text-gris-700" data-id="element-1297" data-mp="2k15ltk6roqgm">Módulo restringido</p>
            <p className="max-w-md text-xs text-gris-500" data-id="element-1298" data-mp="2o4tu65383q2x">
              Solo el rol Administrador puede dar de alta sucursales y su estructura física.
            </p>
          </div>
        </Panel>
      </>;
  }
  const guardar = (sucursal: Sucursal) => {
    if (editando?.esNueva) {
      agregarSucursal(sucursal);
      setAviso(`Sucursal ${sucursal.nombre} (${sucursal.clave}) creada. Agrega sus almacenes en el detalle.`);
    } else {
      actualizarSucursal(sucursal);
      setAviso(`Sucursal ${sucursal.nombre} actualizada.`);
    }
    setEditando(null);
  };
  return <>
      <EncabezadoPagina titulo="Sucursales y almacenes" descripcion="Estructura física donde se resguardan los expedientes: Sucursal → Almacén → Pasillo → Estante → Caja." acciones={<button type="button" onClick={() => setEditando({
      sucursal: sucursalVacia(),
      esNueva: true
    })} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-1300" data-mp="3di2o36e6h2yd">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1301" data-mp="2trz91chsyus5" />
            Agregar sucursal
          </button>} data-id="element-1299" data-mp="33nsslftxvojf" />

      {aviso && <p role="status" className="mb-4 rounded border border-exito-100 bg-exito-50 px-4 py-2 text-xs font-medium text-exito-700" data-id="element-1302" data-mp="dza4jmkzexwd">
          {aviso}
        </p>}

      <Panel data-id="element-1303" data-mp="hhpje3ljijjk">
        {editando && <EditorSucursal sucursal={editando.sucursal} esNueva={editando.esNueva} clavesUsadas={sucursales.filter(s => s.id !== editando.sucursal.id).map(s => s.clave)} onGuardar={guardar} onCancelar={() => setEditando(null)} data-id="element-1304" data-mp="lk45qxof41fl" />}

        <EncabezadoPanel titulo="Sucursales del despacho" descripcion={`${sucursales.length} registradas · ${sucursales.filter(s => s.activa).length} activas · la clave se usa como prefijo del localizador`} data-id="element-1305" data-mp="10a1misk0pour" />

        <div className="overflow-x-auto scroll-fino" data-id="element-1306" data-mp="qh2wr7csbs9k">
          <table className="w-full min-w-[1180px] text-sm" data-id="element-1307" data-mp="vh8i65sgoij7">
            <thead data-id="element-1308" data-mp="1j7uptvyk00so">
              <tr className="bg-gris-50 text-left text-2xs uppercase tracking-wide text-gris-500" data-id="element-1309" data-mp="1j7cju7m3a87k">
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-1310" data-mp="1nz31kyncvy90">
                  Sucursal
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1311" data-mp="3vj79etk5garq">
                  Clave
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1312" data-mp="3rezkowdoyuti">
                  Ciudad y estado
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1313" data-mp="3mjkb4gmpvl2w">
                  Dirección
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1314" data-mp="j0hivcecgbic">
                  Teléfono
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1315" data-mp="e1ec9niptx89">
                  Responsable
                </th>
                <th scope="col" className="px-3 py-2.5 text-right font-semibold" data-id="element-1316" data-mp="99mvg4pd35vb">
                  Almacenes
                </th>
                <th scope="col" className="px-3 py-2.5 text-right font-semibold" data-id="element-1317" data-mp="49qpw110n6dn">
                  Expedientes
                </th>
                <th scope="col" className="px-5 py-2.5 text-right font-semibold" data-id="element-1318" data-mp="x0s7e8s6le0c">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gris-100" data-id="element-1319" data-mp="14drljjv1950d">
              {sucursales.map(s => {
              const susAlmacenes = almacenes.filter(a => a.sucursalId === s.id);
              const cajas = susAlmacenes.reduce((t, a) => t + contarCajas(a), 0);
              const resguardados = expedientes.filter(e => e.sucursalId === s.id).length;
              const responsable = abogadoPorId(s.responsableId);
              return <tr key={s.id} className={`align-top ${s.activa ? '' : 'bg-gris-50/60'}`} data-id="element-1320" data-mp="1s8kght5o8h5v">
                    <td className="px-5 py-3" data-id="element-1321" data-mp="1x02j0lncepsy">
                      <Link to={`/configuracion/sucursales/${s.id}`} className={`text-sm font-semibold hover:underline ${s.activa ? 'text-marino-800' : 'text-gris-400'}`} data-id="element-1322" data-mp="1x2f7ziriq0fi">
                        {s.nombre}
                      </Link>
                      {!s.activa && <span className="ml-2 rounded bg-gris-100 px-1.5 py-0.5 text-2xs font-medium text-gris-500 ring-1 ring-gris-200" data-id="element-1323" data-mp="bt97df80t5c7">
                          Inactiva
                        </span>}
                    </td>
                    <td className="px-3 py-3" data-id="element-1324" data-mp="fwoy5uxesbgz">
                      <span className="rounded bg-marino-50 px-2 py-0.5 font-mono text-2xs font-semibold text-marino-700 ring-1 ring-marino-100" data-id="element-1325" data-mp="fuufyodiuxgl">
                        {s.clave}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gris-700" data-id="element-1326" data-mp="13m9o4xrzc8zr">
                      {s.ciudad}
                      <span className="block text-2xs text-gris-400" data-id="element-1327" data-mp="zkem1r285rkx">{s.estado}</span>
                    </td>
                    <td className="max-w-[16rem] px-3 py-3 text-xs text-gris-600" data-id="element-1328" data-mp="35d4kbqq3ma70">{s.direccion}</td>
                    <td className="tabular px-3 py-3 text-xs text-gris-700" data-id="element-1329" data-mp="31d2ssae2ux5g">{s.telefono || '—'}</td>
                    <td className="max-w-[14rem] px-3 py-3 text-xs text-gris-700" data-id="element-1330" data-mp="2r848hx3s7vin">
                      {responsable?.nombre ?? 'Sin asignar'}
                      {responsable && <span className="tabular block text-2xs text-gris-400" data-id="element-1331" data-mp="2hj2uudkplq7u">
                          Cédula {responsable.cedula}
                        </span>}
                    </td>
                    <td className="tabular px-3 py-3 text-right text-xs text-gris-700" data-id="element-1332" data-mp="27k3rnaipqms8">
                      {susAlmacenes.length}
                      <span className="block text-2xs text-gris-400" data-id="element-1333" data-mp="2ckrjtzioh9o1">{cajas} cajas</span>
                    </td>
                    <td className="tabular px-3 py-3 text-right text-xs text-gris-700" data-id="element-1334" data-mp="1ylrwqwatd2tz">
                      {resguardados}
                    </td>
                    <td className="px-5 py-3" data-id="element-1335" data-mp="3sxge154iphhj">
                      <div className="flex items-center justify-end gap-1" data-id="element-1336" data-mp="3svn3heqsad49">
                        <Link to={`/configuracion/sucursales/${s.id}`} aria-label={`Ver detalle de ${s.nombre}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-gris-200 hover:text-marino-700" data-id="element-1337" data-mp="1qwt2x4ywb5i">
                          <ArrowRightIcon className="h-4 w-4" data-id="element-1338" data-mp="wjo0gta2zveh" />
                        </Link>
                        <button type="button" onClick={() => setEditando({
                      sucursal: s,
                      esNueva: false
                    })} aria-label={`Editar ${s.nombre}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-gris-200 hover:text-marino-700" data-id="element-1339" data-mp="rm5t37bppo8l">
                          <PencilIcon className="h-4 w-4" data-id="element-1340" data-mp="27zmhpkfexnqs" />
                        </button>
                        <button type="button" onClick={() => {
                      alternarSucursal(s.id);
                      setAviso(`${s.nombre} quedó ${s.activa ? 'inactiva' : 'activa'}.`);
                    }} aria-label={`${s.activa ? 'Desactivar' : 'Activar'} ${s.nombre}`} className={`flex h-8 w-8 items-center justify-center rounded border border-transparent transition-colors duration-150 hover:border-gris-200 ${s.activa ? 'text-gris-400 hover:text-ambar-600' : 'text-exito-600 hover:text-exito-700'}`} data-id="element-1341" data-mp="27xrx6781zxn8">
                          <PowerIcon className="h-4 w-4" data-id="element-1342" data-mp="3kw1uf1pqa47d" />
                        </button>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Panel>
    </>;
}